import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  color: #374151;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ActionButton = styled.button`
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #1f2937;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NotificationItem = styled.div`
  border: 1px solid ${props => props.$unread ? '#86efac' : '#e5e7eb'};
  background: ${props => props.$unread ? '#f0fdf4' : '#ffffff'};
  border-radius: 10px;
  padding: 12px;
`;

const NotificationTitle = styled.div`
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const NotificationMessage = styled.div`
  color: #4b5563;
  font-size: 14px;
`;

const NotificationMeta = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 12px;
`;

export default function AdminNotifications() {
  const { session } = useAuthContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!session?.user?.id) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('notifications')
      .select('id, title, message, is_read, created_at, order_id, brand_id, type')
      .eq('recipient_profile_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('Failed to fetch notifications:', error);
      setItems([]);
    } else {
      setItems(data || []);
    }
    setLoading(false);
  }, [session?.user?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = useMemo(
    () => items.filter((item) => item.is_read === false).length,
    [items]
  );

  const markAsRead = async (id) => {
    setUpdatingId(id);
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id)
      .eq('recipient_profile_id', session?.user?.id);

    if (error) {
      console.error('Failed to mark notification as read:', error);
    } else {
      setItems((prev) => prev.map((item) => (item.id === id ? { ...item, is_read: true } : item)));
    }
    setUpdatingId(null);
  };

  const markAllAsRead = async () => {
    setMarkingAll(true);
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('recipient_profile_id', session?.user?.id)
      .eq('is_read', false);

    if (error) {
      console.error('Failed to mark all notifications as read:', error);
    } else {
      setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
    }
    setMarkingAll(false);
  };

  return (
    <Container>
      <HeaderRow>
        <Title>Notifications</Title>
        <ActionButton onClick={markAllAsRead} disabled={markingAll || unreadCount === 0}>
          {markingAll ? 'Marking...' : `Mark all as read (${unreadCount})`}
        </ActionButton>
      </HeaderRow>
      <Card>
        {loading ? (
          <div>Loading notifications...</div>
        ) : items.length === 0 ? (
          <div>No notifications yet.</div>
        ) : (
          <List>
            {items.map((item) => (
              <NotificationItem key={item.id} $unread={!item.is_read}>
                <NotificationTitle>{item.title}</NotificationTitle>
                <NotificationMessage>{item.message}</NotificationMessage>
                <NotificationMeta>
                  <span>{new Date(item.created_at).toLocaleString()}</span>
                  {!item.is_read && (
                    <ActionButton onClick={() => markAsRead(item.id)} disabled={updatingId === item.id}>
                      {updatingId === item.id ? 'Saving...' : 'Mark as read'}
                    </ActionButton>
                  )}
                </NotificationMeta>
              </NotificationItem>
            ))}
          </List>
        )}
      </Card>
    </Container>
  );
}
